import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { BookOpen, CheckCircle2, Trophy, ArrowRight, Flame, Lock, Sparkles } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import UpgradeModal from "../components/UpgradeModal";

const ICONS = {
  "hard-hat": "🪖",
  wrench: "🔧",
  droplet: "💧",
  zap: "⚡",
  hammer: "🔨",
  "paint-roller": "🎨",
  "grid-3x3": "🔲",
  users: "👥",
};

const SOFT_PROMPT_KEY = "p1_soft_prompt_seen";

export default function Dashboard() {
  const { user } = useAuth();
  const [courses, setCourses] = useState([]);
  const [progress, setProgress] = useState({ lessons_completed: [], attempts: [] });
  const [cert, setCert] = useState(null);
  const [showSoft, setShowSoft] = useState(false);

  const paid = !!user && user.plan && user.plan !== "free";

  useEffect(() => {
    (async () => {
      const [c, p, ct] = await Promise.all([
        api.get("/courses"),
        api.get("/progress"),
        api.get("/certificates/me"),
      ]);
      setCourses(c.data);
      setProgress(p.data);
      setCert(ct.data.certificate);
    })();
  }, []);

  const passedCourseIds = useMemo(() => {
    const s = new Set();
    progress.attempts.filter((a) => a.passed).forEach((a) => s.add(a.course_id));
    return s;
  }, [progress]);

  const totalPassed = passedCourseIds.size;
  const total = courses.length || 8;
  const percent = Math.round((totalPassed / total) * 100);

  // 25% soft prompt: free user has completed ≥25% of total lessons (preview area)
  useEffect(() => {
    if (!user || paid) return;
    if (courses.length === 0) return;
    const totalLessons = 24; // approximate total across 8 modules; could be derived if we fetched lessons
    const completed = progress.lessons_completed.length;
    const ratio = completed / Math.max(totalLessons, 1);
    const seen = typeof window !== "undefined" && localStorage.getItem(SOFT_PROMPT_KEY);
    if (ratio >= 0.25 && !seen) {
      setShowSoft(true);
      localStorage.setItem(SOFT_PROMPT_KEY, "1");
    }
  }, [progress, courses, user, paid]);

  return (
    <div className="p-6 lg:p-10 max-w-7xl" data-testid="dashboard-page">
      <UpgradeModal open={showSoft} onClose={() => setShowSoft(false)} reason="soft" />

      <div className="flex items-end justify-between gap-6 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">
            {paid ? `${user.plan?.toUpperCase()} PLAN · Cohort 01` : "Free preview · Cohort 01"}
          </div>
          <h1 className="font-display text-4xl font-bold tracking-tight text-slate-950 mt-1">
            Welcome back, {user?.name?.split(" ")[0] || "Trainee"}.
          </h1>
          <p className="text-slate-600 mt-2">
            {cert
              ? "You're Platinum 1 Certified. Show it off."
              : paid
              ? "Pass all 8 module quizzes to unlock your Platinum 1 Certified credential."
              : "First 2 modules are free. Upgrade anytime to unlock quizzes and your certificate."}
          </p>
        </div>
        {cert ? (
          <Link
            to="/certificate"
            data-testid="view-cert-btn"
            className="bg-orange-600 hover:bg-orange-700 text-white px-5 py-3 rounded-sm font-semibold inline-flex items-center gap-2"
          >
            <Trophy className="w-4 h-4" /> View Certificate
          </Link>
        ) : paid ? (
          <Link
            to="/courses"
            data-testid="continue-learning-btn"
            className="bg-orange-600 hover:bg-orange-700 text-white px-5 py-3 rounded-sm font-semibold inline-flex items-center gap-2"
          >
            Continue learning <ArrowRight className="w-4 h-4" />
          </Link>
        ) : (
          <Link
            to="/pricing"
            data-testid="upgrade-cta-btn"
            className="bg-orange-600 hover:bg-orange-700 text-white px-5 py-3 rounded-sm font-semibold inline-flex items-center gap-2"
          >
            <Sparkles className="w-4 h-4" /> Upgrade to get certified
          </Link>
        )}
      </div>

      {/* Plan banner for free users */}
      {!paid && (
        <div data-testid="free-plan-banner" className="mt-8 border border-orange-200 bg-orange-50 rounded-sm p-5 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-600 grid place-items-center rounded-sm text-white">
              <Sparkles className="w-5 h-5" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.2em] font-bold text-orange-700">Free preview</div>
              <div className="font-display font-bold text-slate-950 mt-0.5">
                You have access to <strong>2 of 8 modules</strong>. Quizzes & certificate require any plan.
              </div>
            </div>
          </div>
          <Link to="/pricing" data-testid="banner-upgrade-btn" className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-sm font-semibold text-sm inline-flex items-center gap-2">
            See plans <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      )}

      {/* KPI */}
      <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
        <KPI label="Modules Enrolled" value={total} icon={<BookOpen className="w-4 h-4" />} testid="kpi-modules" />
        <KPI label="Modules Passed" value={`${totalPassed} / ${total}`} icon={<CheckCircle2 className="w-4 h-4" />} testid="kpi-passed" />
        <KPI label="Certificate" value={cert ? "Issued" : paid ? "Pending" : "Locked"} icon={<Trophy className="w-4 h-4" />} testid="kpi-cert" accent={!!cert} />
      </div>

      {/* Progress bar */}
      <div className="mt-10 border border-slate-200 rounded-sm p-6">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Overall progress</div>
            <div className="font-display font-bold text-2xl text-slate-950 mt-1">{percent}% complete</div>
          </div>
          <Flame className="w-6 h-6 text-orange-600" />
        </div>
        <div className="w-full h-2 bg-slate-100 overflow-hidden rounded-sm">
          <div
            data-testid="overall-progress-bar"
            className="h-full bg-orange-600 transition-all duration-500"
            style={{ width: `${percent}%` }}
          />
        </div>
      </div>

      {/* Course list */}
      <div className="mt-10">
        <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900">Your modules</h2>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {courses.map((c) => {
            const passed = passedCourseIds.has(c.course_id);
            const isLocked = !paid && !c.is_preview;
            return (
              <Link
                to={`/courses/${c.course_id}`}
                key={c.course_id}
                data-testid={`course-card-${c.slug}`}
                className={`border rounded-sm overflow-hidden bg-white hover:-translate-y-1 hover:shadow-md transition-all relative ${
                  isLocked ? "border-slate-200 opacity-90" : "border-slate-200"
                }`}
              >
                <div className="aspect-[16/9] bg-slate-100 overflow-hidden relative">
                  {c.thumbnail ? (
                    <img src={c.thumbnail} alt="" className={`w-full h-full object-cover ${isLocked ? "grayscale" : ""}`} />
                  ) : (
                    <div className="w-full h-full grid place-items-center text-4xl">{ICONS[c.icon] || "🛠"}</div>
                  )}
                  {passed && (
                    <div className="absolute top-3 right-3 bg-green-600 text-white text-xs font-bold uppercase tracking-[0.15em] px-2 py-1 rounded-sm">
                      Passed
                    </div>
                  )}
                  {isLocked && (
                    <div className="absolute top-3 right-3 bg-slate-950 text-white text-xs font-bold uppercase tracking-[0.15em] px-2 py-1 rounded-sm inline-flex items-center gap-1">
                      <Lock className="w-3 h-3" /> Locked
                    </div>
                  )}
                  {c.is_preview && !paid && !passed && (
                    <div className="absolute top-3 left-3 bg-orange-600 text-white text-xs font-bold uppercase tracking-[0.15em] px-2 py-1 rounded-sm">
                      Free preview
                    </div>
                  )}
                </div>
                <div className="p-5">
                  <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">
                    Module {String(c.order).padStart(2, "0")}
                  </div>
                  <div className="font-display font-bold text-slate-950 text-lg mt-2 leading-snug">{c.title}</div>
                  <div className="text-sm text-slate-600 mt-2 line-clamp-2">{c.description}</div>
                  <div className="mt-4 inline-flex items-center gap-1 text-orange-700 text-sm font-semibold">
                    {isLocked ? "Preview" : passed ? "Review module" : "Start module"} <ArrowRight className="w-4 h-4" />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function KPI({ label, value, icon, testid, accent }) {
  return (
    <div
      data-testid={testid}
      className={`border rounded-sm p-5 ${accent ? "border-orange-600 bg-orange-50" : "border-slate-200 bg-white"}`}
    >
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">{label}</div>
        <div className={`w-7 h-7 grid place-items-center rounded-sm ${accent ? "bg-orange-600 text-white" : "bg-slate-100 text-slate-700"}`}>
          {icon}
        </div>
      </div>
      <div className="font-display font-black text-3xl tracking-tighter mt-3 text-slate-950">{value}</div>
    </div>
  );
}
